/*
 * Dashboard task summary
 *
 * Purpose: Provide a compact "My Tasks" surface on the CRM dashboard without duplicating the full Tasks page workflow.
 * Recent changes: Dashboard task rows open an inline panel; headings are centered with controls using compact 4px spacing.
 * - Requires a valid due date before saving an editable dashboard task.
 * - Uses one category-control surface and responsive task cards below the desktop breakpoint.
 */

import { FC, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ArrowsUpDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { THEME_CLASSES } from '../../constants/themeConstants';
import {
  Task,
  TaskPriority,
  TaskStatus,
  TASK_DUE_DATE_PAST_ERROR,
  TASK_DUE_DATE_REQUIRED_ERROR,
  canMutateTask,
  getTaskDueDateValidationError,
  getTaskStatusLabel,
  getTodayDateValue,
  isTaskClosed,
} from '../../models/Task';
import { updateTask } from '../../services/taskService';
import { Button, Input, Textarea } from '../primitives';

type TaskDashboardFilter = 'overview' | 'overdue' | 'today' | 'next7' | 'waiting' | 'noNextStep';
type TaskDashboardSort = 'dueAsc' | 'dueDesc' | 'noDueFirst';

interface DashboardTasksProps {
  tasks: Task[];
  isLoading?: boolean;
  currentUserId?: number | null;
}

interface TaskMetric {
  key: TaskDashboardFilter;
  label: string;
  value: number;
  description: string;
  icon: JSX.Element;
  iconClassName: string;
}

const PRIORITY_STYLES: Record<TaskPriority, string> = {
  High: 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-200',
  Medium: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-200',
  Low: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-200',
};

const STATUS_STYLES: Record<TaskStatus, string> = {
  Open: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-200',
  Waiting: 'bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-200',
  Done: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  Canceled: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
};

const SORT_LABELS: Record<TaskDashboardSort, string> = {
  dueAsc: 'Due Date',
  dueDesc: 'Due Date: Latest',
  noDueFirst: 'No Due Date First',
};

const TASK_STATUS_OPTIONS: TaskStatus[] = ['Open', 'Waiting', 'Done', 'Canceled'];
const TASK_PRIORITY_OPTIONS: TaskPriority[] = ['High', 'Medium', 'Low'];
const AVATAR_STYLES = [
  'bg-blue-600 text-white',
  'bg-emerald-600 text-white',
  'bg-violet-600 text-white',
  'bg-rose-600 text-white',
  'bg-amber-500 text-white',
];

const addDays = (dateValue: string, days: number): string => {
  const date = new Date(`${dateValue}T00:00:00`);
  date.setDate(date.getDate() + days);
  return getTodayDateValue(date);
};

const formatTaskDate = (dueDate: string | null) => {
  if (!dueDate) {
    return {
      dateLabel: 'No date',
      relativeLabel: '',
      className: THEME_CLASSES.text.secondary,
    };
  }

  const today = getTodayDateValue();
  const due = new Date(`${dueDate}T00:00:00`);
  const now = new Date(`${today}T00:00:00`);
  const diffDays = Math.round((due.getTime() - now.getTime()) / 86400000);
  const dateLabel = due.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  if (diffDays < 0) {
    return {
      dateLabel,
      relativeLabel: `${Math.abs(diffDays)} ${Math.abs(diffDays) === 1 ? 'day' : 'days'} overdue`,
      className: 'text-rose-600 dark:text-rose-300',
    };
  }

  if (diffDays === 0) {
    return {
      dateLabel,
      relativeLabel: 'Due today',
      className: 'text-amber-600 dark:text-amber-300',
    };
  }

  return {
    dateLabel,
    relativeLabel: `in ${diffDays} ${diffDays === 1 ? 'day' : 'days'}`,
    className: THEME_CLASSES.text.secondary,
  };
};

const getInitials = (task: Task) => {
  const source = task.assigneeName && task.assigneeName !== 'Unassigned'
    ? task.assigneeName
    : task.accountName;

  return source
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

const getFilteredTasks = (tasks: Task[], filter: TaskDashboardFilter, today: string, nextSevenDays: string) => {
  return tasks.filter((task) => {
    if (filter === 'overview') {
      return true;
    }

    if (filter === 'overdue') {
      return Boolean(task.dueDate && task.dueDate < today);
    }

    if (filter === 'today') {
      return task.dueDate === today;
    }

    if (filter === 'next7') {
      return Boolean(task.dueDate && task.dueDate > today && task.dueDate <= nextSevenDays);
    }

    if (filter === 'waiting') {
      return task.status === 'Waiting';
    }

    return !task.dueDate && task.status === 'Open';
  });
};

const getTaskSearchValue = (task: Task) => {
  return [
    task.title,
    task.description,
    task.accountName,
    task.contactName,
    task.assigneeName,
    task.sourceLabel,
    task.priority,
    getTaskStatusLabel(task.status),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

const searchDashboardTasks = (tasks: Task[], searchQuery: string) => {
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  if (!normalizedSearchQuery) {
    return tasks;
  }

  return tasks.filter((task) => getTaskSearchValue(task).includes(normalizedSearchQuery));
};

const sortDashboardTasks = (tasks: Task[], sortMode: TaskDashboardSort) => {
  return [...tasks].sort((left, right) => {
    if (sortMode === 'noDueFirst') {
      if (!left.dueDate && right.dueDate) {
        return -1;
      }

      if (left.dueDate && !right.dueDate) {
        return 1;
      }
    }

    if (!left.dueDate && !right.dueDate) {
      return new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime();
    }

    if (!left.dueDate) {
      return 1;
    }

    if (!right.dueDate) {
      return -1;
    }

    return sortMode === 'dueDesc'
      ? right.dueDate.localeCompare(left.dueDate)
      : left.dueDate.localeCompare(right.dueDate);
  });
};

const DashboardTaskPanel: FC<{
  task: Task;
  currentUserId?: number | null;
  onClose: () => void;
  onTaskSaved: (task: Task) => void;
}> = ({ task, currentUserId, onClose, onTaskSaved }) => {
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description);
  const [status, setStatus] = useState<TaskStatus>(task.status);
  const [priority, setPriority] = useState<TaskPriority>(task.priority);
  const [dueDate, setDueDate] = useState(task.dueDate ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canEditTask = canMutateTask(task, currentUserId ?? null);
  const minDueDate = getTodayDateValue();

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Task title is required.');
      return;
    }

    const dueDateValidationError = getTaskDueDateValidationError(dueDate, minDueDate);
    if (dueDateValidationError) {
      setError(dueDateValidationError);
      return;
    }

    if (!canEditTask) {
      setError('You can view this task, but only the creator or an assignee can edit it.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const savedTask = await updateTask(task.id, {
        title: title.trim(),
        description: description.trim(),
        accountId: task.accountId,
        contactId: task.contactId,
        sourceNoteId: task.sourceNoteId,
        dueDate: dueDate || null,
        status,
        priority,
        assigneeIds: task.assigneeIds,
      });

      onTaskSaved(savedTask);
    } catch (saveError) {
      console.error('Failed to update dashboard task:', saveError);
      setError('Unable to update this task right now.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <aside className={`flex min-h-[33rem] flex-col border-l ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background}`}>
      <div className={`flex items-start justify-between gap-4 border-b ${THEME_CLASSES.card.border} px-5 py-4`}>
        <div className="min-w-0">
          <h3 className={`truncate text-lg font-semibold ${THEME_CLASSES.text.primary}`}>{task.title}</h3>
          <p className={`mt-1 text-sm ${THEME_CLASSES.text.secondary}`}>{canEditTask ? 'Editing Task' : 'Viewing Task'}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className={`rounded-lg px-2 py-1 text-sm font-medium ${THEME_CLASSES.text.secondary} hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-900 dark:hover:text-slate-50`}
          aria-label="Close task panel"
        >
          x
        </button>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
        <div>
          <label className={`mb-2 block text-xs font-semibold uppercase tracking-[0.16em] ${THEME_CLASSES.text.secondary}`} htmlFor="dashboard-task-title">
            Task
          </label>
          <Input
            id="dashboard-task-title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            disabled={!canEditTask}
          />
        </div>

        <div>
          <label className={`mb-2 block text-xs font-semibold uppercase tracking-[0.16em] ${THEME_CLASSES.text.secondary}`} htmlFor="dashboard-task-description">
            Description
          </label>
          <Textarea
            id="dashboard-task-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={7}
            disabled={!canEditTask}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={`mb-2 block text-xs font-semibold uppercase tracking-[0.16em] ${THEME_CLASSES.text.secondary}`} htmlFor="dashboard-task-status">
              Status
            </label>
            <select
              id="dashboard-task-status"
              value={status}
              onChange={(event) => setStatus(event.target.value as TaskStatus)}
              className={`w-full rounded-lg border ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text} px-3 py-2 text-sm`}
              disabled={!canEditTask}
            >
              {TASK_STATUS_OPTIONS.map((taskStatus) => (
                <option key={taskStatus} value={taskStatus}>
                  {getTaskStatusLabel(taskStatus)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={`mb-2 block text-xs font-semibold uppercase tracking-[0.16em] ${THEME_CLASSES.text.secondary}`} htmlFor="dashboard-task-priority">
              Priority
            </label>
            <select
              id="dashboard-task-priority"
              value={priority}
              onChange={(event) => setPriority(event.target.value as TaskPriority)}
              className={`w-full rounded-lg border ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text} px-3 py-2 text-sm`}
              disabled={!canEditTask}
            >
              {TASK_PRIORITY_OPTIONS.map((taskPriority) => (
                <option key={taskPriority} value={taskPriority}>
                  {taskPriority}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className={`mb-2 block text-xs font-semibold uppercase tracking-[0.16em] ${THEME_CLASSES.text.secondary}`} htmlFor="dashboard-task-due-date">
            Due date
          </label>
          <Input
            id="dashboard-task-due-date"
            type="date"
            min={minDueDate}
            value={dueDate}
            required
            onChange={(event) => {
              setDueDate(event.target.value);
              if (
                (error === TASK_DUE_DATE_REQUIRED_ERROR || error === TASK_DUE_DATE_PAST_ERROR)
                && !getTaskDueDateValidationError(event.target.value, minDueDate)
              ) {
                setError(null);
              }
            }}
            disabled={!canEditTask}
          />
        </div>

        <div className={`rounded-lg border ${THEME_CLASSES.card.border} px-4 py-3 text-sm`}>
          <div className={`font-semibold ${THEME_CLASSES.text.primary}`}>{task.accountName}</div>
          <div className={`mt-1 ${THEME_CLASSES.text.secondary}`}>
            {task.contactName ? `Contact: ${task.contactName}` : task.sourceLabel}
          </div>
          <div className={`mt-1 ${THEME_CLASSES.text.secondary}`}>Assignee: {task.assigneeName}</div>
        </div>

        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
            {error}
          </div>
        )}
      </div>

      <div className={`flex items-center justify-end gap-3 border-t ${THEME_CLASSES.card.border} px-5 py-4`}>
        <Button onClick={onClose} variant="secondary" size="sm">
          {canEditTask ? 'Cancel' : 'Close'}
        </Button>
        {canEditTask && (
          <Button onClick={handleSave} size="sm" disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Task'}
          </Button>
        )}
      </div>
    </aside>
  );
};

const DashboardTasks: FC<DashboardTasksProps> = ({ tasks, isLoading = false, currentUserId = null }) => {
  const [selectedFilter, setSelectedFilter] = useState<TaskDashboardFilter>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortMode, setSortMode] = useState<TaskDashboardSort>('dueAsc');
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);

  const today = getTodayDateValue();
  const nextSevenDays = addDays(today, 7);

  const activeTasks = useMemo(
    () => tasks.filter((task) => !task.deletedAt && !isTaskClosed(task.status)),
    [tasks]
  );

  const metrics = useMemo<TaskMetric[]>(() => {
    const overdue = activeTasks.filter((task) => task.dueDate && task.dueDate < today).length;
    const dueToday = activeTasks.filter((task) => task.dueDate === today).length;
    const nextSeven = activeTasks.filter((task) => task.dueDate && task.dueDate > today && task.dueDate <= nextSevenDays).length;
    const waiting = activeTasks.filter((task) => task.status === 'Waiting').length;
    const noNextStep = activeTasks.filter((task) => !task.dueDate && task.status === 'Open').length;

    return [
      {
        key: 'overdue',
        label: 'Overdue',
        value: overdue,
        description: 'Needs attention',
        iconClassName: 'text-rose-500',
        icon: (
          <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 9v4m0 4h.01M10.29 3.86l-7.12 12.3A2 2 0 004.88 19h14.24a2 2 0 001.71-2.84l-7.12-12.3a2 2 0 00-3.42 0z" />
          </svg>
        ),
      },
      {
        key: 'today',
        label: 'Due Today',
        value: dueToday,
        description: 'Tasks due today',
        iconClassName: 'text-blue-500',
        icon: (
          <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      },
      {
        key: 'next7',
        label: 'Next 7 Days',
        value: nextSeven,
        description: 'Upcoming tasks',
        iconClassName: 'text-amber-500',
        icon: (
          <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      },
      {
        key: 'waiting',
        label: 'Waiting',
        value: waiting,
        description: 'Waiting on others',
        iconClassName: 'text-violet-500',
        icon: (
          <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
      },
      {
        key: 'noNextStep',
        label: 'No Next Step',
        value: noNextStep,
        description: 'Needs next step',
        iconClassName: THEME_CLASSES.text.secondary,
        icon: (
          <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8.23 9a3.75 3.75 0 117.04 1.8c-.9 1.16-2.02 1.52-2.77 2.45-.36.45-.5.84-.5 1.75m0 3h.01" />
          </svg>
        ),
      },
    ];
  }, [activeTasks, nextSevenDays, today]);

  const filteredTasks = useMemo(
    () => {
      const filteredByDashboardFilter = getFilteredTasks(activeTasks, selectedFilter, today, nextSevenDays);
      const searchedTasks = searchDashboardTasks(filteredByDashboardFilter, searchQuery);
      return sortDashboardTasks(searchedTasks, sortMode).slice(0, 5);
    },
    [activeTasks, nextSevenDays, searchQuery, selectedFilter, sortMode, today]
  );

  const selectedTask = useMemo(
    () => tasks.find((task) => task.id === selectedTaskId) ?? null,
    [selectedTaskId, tasks]
  );

  useEffect(() => {
    if (selectedTaskId !== null && !selectedTask) {
      setSelectedTaskId(null);
    }
  }, [selectedTask, selectedTaskId]);

  const dashboardFilters = useMemo<TaskMetric[]>(() => [
    {
      key: 'overview',
      label: 'All',
      value: activeTasks.length,
      description: 'Active tasks',
      iconClassName: 'text-blue-500',
      icon: (
        <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2m-4 0a2 2 0 104 0m-4 0a2 2 0 014 0m-4 5h6m-6 4h6m-6 4h3" />
        </svg>
      ),
    },
    ...metrics,
  ], [activeTasks.length, metrics]);

  return (
    <section className="space-y-1" aria-labelledby="dashboard-tasks-heading">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <h2 id="dashboard-tasks-heading" className={`m-0 text-xl font-semibold ${THEME_CLASSES.text.primary}`}>
          My Tasks
        </h2>
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center xl:justify-end">
          <div className="relative min-w-0 sm:w-64">
            <MagnifyingGlassIcon className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${THEME_CLASSES.text.secondary}`} aria-hidden="true" />
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search tasks..."
              aria-label="Search tasks"
              className={`h-9 w-full rounded-lg border ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text} ${THEME_CLASSES.input.placeholder} pl-9 pr-3 text-sm shadow-sm outline-none transition-colors focus:border-blue-400 focus:ring-2 focus:ring-blue-100 dark:focus:border-blue-500 dark:focus:ring-blue-950`}
            />
          </div>
          <div className="relative sm:w-52">
            <ArrowsUpDownIcon className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${THEME_CLASSES.text.secondary}`} aria-hidden="true" />
            <select
              value={sortMode}
              onChange={(event) => setSortMode(event.target.value as TaskDashboardSort)}
              aria-label="Sort tasks"
              className={`h-9 w-full appearance-none rounded-lg border ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text} pl-9 pr-8 text-sm font-medium shadow-sm outline-none transition-colors focus:border-blue-400 focus:ring-2 focus:ring-blue-100 dark:focus:border-blue-500 dark:focus:ring-blue-950`}
            >
              {(Object.keys(SORT_LABELS) as TaskDashboardSort[]).map((sortOption) => (
                <option key={sortOption} value={sortOption}>
                  Sort: {SORT_LABELS[sortOption]}
                </option>
              ))}
            </select>
            <svg className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 ${THEME_CLASSES.text.secondary}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
            </svg>
          </div>
          <Link to="/tasks" className="text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline dark:text-blue-300 dark:hover:text-blue-200">
            View all tasks
          </Link>
        </div>
      </div>

      <div className={`rounded-2xl border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} shadow-sm`}>
        <div
          className="flex gap-2 overflow-x-auto p-2 lg:grid lg:grid-cols-6 lg:gap-0 lg:divide-x lg:divide-slate-200 lg:overflow-visible dark:lg:divide-slate-800"
          aria-label="Task categories"
        >
          {dashboardFilters.map((metric) => (
            <button
              key={metric.key}
              type="button"
              onClick={() => setSelectedFilter(metric.key)}
              aria-pressed={selectedFilter === metric.key}
              className={`flex min-w-[9rem] shrink-0 items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-slate-50 lg:min-w-0 lg:rounded-none dark:hover:bg-slate-900 ${selectedFilter === metric.key ? 'bg-blue-50/70 ring-2 ring-inset ring-blue-500 lg:ring-0 dark:bg-blue-950/20' : ''}`}
            >
              <span className={metric.iconClassName}>{metric.icon}</span>
              <span>
                <span className={`block text-xl font-semibold ${THEME_CLASSES.text.primary}`}>{metric.value}</span>
                <span className={`block text-sm font-semibold ${THEME_CLASSES.text.primary}`}>{metric.label}</span>
                <span className={`block text-xs ${THEME_CLASSES.text.secondary}`}>{metric.description}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className={`overflow-hidden rounded-2xl border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} shadow-sm`}>
        <div className={`grid ${selectedTask ? 'xl:grid-cols-[minmax(0,1fr)_25rem]' : 'grid-cols-1'}`}>
          <div className="lg:hidden" data-testid="responsive-task-list">
            {isLoading ? (
              <div className="space-y-3 p-4">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="h-36 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-900" />
                ))}
              </div>
            ) : filteredTasks.length > 0 ? (
              filteredTasks.map((task, index) => {
                const dueDate = formatTaskDate(task.dueDate);

                return (
                  <article key={task.id} className={`space-y-3 border-b ${THEME_CLASSES.card.border} p-4 last:border-b-0`}>
                    <div className="flex min-w-0 items-start gap-3">
                      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${AVATAR_STYLES[index % AVATAR_STYLES.length]}`}>
                        {getInitials(task)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <button
                          type="button"
                          onClick={() => setSelectedTaskId(task.id)}
                          className={`block w-full text-left font-semibold ${THEME_CLASSES.text.primary} hover:underline`}
                          aria-label={`Open task ${task.title}`}
                        >
                          {task.title}
                        </button>
                        {task.description && (
                          <div className={`mt-1 line-clamp-2 text-xs ${THEME_CLASSES.text.secondary}`}>{task.description}</div>
                        )}
                      </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="min-w-0">
                        <div className={`text-[11px] font-semibold uppercase tracking-wide ${THEME_CLASSES.text.secondary}`}>Related to</div>
                        <Link to={`/accounts/${task.accountId}`} className="mt-1 block truncate text-sm font-semibold text-blue-600 hover:underline dark:text-blue-300">
                          {task.accountName}
                        </Link>
                        <div className={`mt-1 truncate text-xs ${THEME_CLASSES.text.secondary}`}>
                          {task.contactName ? `Contact: ${task.contactName}` : task.sourceLabel}
                        </div>
                      </div>
                      <div>
                        <div className={`text-[11px] font-semibold uppercase tracking-wide ${THEME_CLASSES.text.secondary}`}>Due</div>
                        <div className={`mt-1 text-sm font-medium ${dueDate.className}`}>{dueDate.dateLabel}</div>
                        {dueDate.relativeLabel && (
                          <div className={`mt-1 text-xs ${dueDate.className}`}>{dueDate.relativeLabel}</div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2" aria-label="Priority and status">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${PRIORITY_STYLES[task.priority]}`}>
                        {task.priority}
                      </span>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_STYLES[task.status]}`}>
                        {getTaskStatusLabel(task.status)}
                      </span>
                    </div>
                  </article>
                );
              })
            ) : (
              <div className={`flex h-40 items-center justify-center px-6 text-center text-sm ${THEME_CLASSES.text.secondary}`}>
                No tasks match this dashboard view.
              </div>
            )}
          </div>

          <div className="hidden overflow-x-auto lg:block" data-testid="desktop-task-list">
            <div className={`grid min-w-[58rem] grid-cols-[2.2rem_minmax(15rem,2fr)_minmax(12rem,1.4fr)_minmax(8rem,0.9fr)_minmax(8rem,0.8fr)] gap-4 border-b ${THEME_CLASSES.card.border} px-4 py-3 text-[11px] font-semibold uppercase ${THEME_CLASSES.text.secondary}`}>
              <div aria-hidden="true" />
              <div>Task</div>
              <div>Related To</div>
              <div>Due Date</div>
              <div>Status</div>
            </div>

            {isLoading ? (
              <div className="min-w-[58rem] space-y-3 p-4">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="h-14 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-900" />
                ))}
              </div>
            ) : filteredTasks.length > 0 ? (
              filteredTasks.map((task, index) => {
                const dueDate = formatTaskDate(task.dueDate);

                return (
                  <div
                    key={task.id}
                    className={`grid min-w-[58rem] grid-cols-[2.2rem_minmax(15rem,2fr)_minmax(12rem,1.4fr)_minmax(8rem,0.9fr)_minmax(8rem,0.8fr)] gap-4 border-b ${THEME_CLASSES.card.border} px-4 py-4 text-sm last:border-b-0`}
                  >
                    <div className="flex items-start justify-center pt-1">
                      <span className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ${AVATAR_STYLES[index % AVATAR_STYLES.length]}`}>
                        {getInitials(task)}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <button
                        type="button"
                        onClick={() => setSelectedTaskId(task.id)}
                        className={`block w-full truncate text-left font-semibold ${THEME_CLASSES.text.primary} hover:underline`}
                        aria-label={`Open task ${task.title}`}
                      >
                        {task.title}
                      </button>
                      {task.description && (
                        <div className={`mt-1 truncate text-xs ${THEME_CLASSES.text.secondary}`}>{task.description}</div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <Link to={`/accounts/${task.accountId}`} className="block truncate font-semibold text-blue-600 hover:underline dark:text-blue-300">
                        {task.accountName}
                      </Link>
                      <div className={`mt-1 truncate text-xs ${THEME_CLASSES.text.secondary}`}>
                        {task.contactName ? `Contact: ${task.contactName}` : task.sourceLabel}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className={`font-medium ${dueDate.className}`}>{dueDate.dateLabel}</div>
                      {dueDate.relativeLabel && (
                        <div className={`mt-1 text-xs ${dueDate.className}`}>{dueDate.relativeLabel}</div>
                      )}
                    </div>
                    <div className="flex flex-wrap items-start gap-2">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${PRIORITY_STYLES[task.priority]}`}>
                        {task.priority}
                      </span>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_STYLES[task.status]}`}>
                        {getTaskStatusLabel(task.status)}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className={`flex h-40 min-w-[58rem] items-center justify-center px-6 text-center text-sm ${THEME_CLASSES.text.secondary}`}>
                No tasks match this dashboard view.
              </div>
            )}
          </div>
          {selectedTask && (
            <DashboardTaskPanel
              key={selectedTask.id}
              task={selectedTask}
              currentUserId={currentUserId}
              onClose={() => setSelectedTaskId(null)}
              onTaskSaved={() => setSelectedTaskId(null)}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default DashboardTasks;
